import { BoardLayout, BoardTopology } from './board/board-view';
import { ColourType } from './colours/colour-type';
import { ResourceHolding } from './resources';
import { CatanState, StateType } from './state/state';

export interface CatanView {
  creatorPlayerId: string;
  accessingPlayerId: string;
  lockState: number;
  state: CatanState;
  players: CatanPlayer[];
  topology: BoardTopology;
  layout: BoardLayout;
  settlements: PlacedSettlement[];
  cities: PlacedCity[];
  roads: PlacedRoad[];
  /**
   * The cell the robber blocks, or null before it has ever moved. A cell the
   * robber blocks pays nothing.
   */
  robberCellId?: number | null;
  /**
   * The accessing player's own hand, and only theirs: a player sees what they
   * hold and everyone else is a `cardCount` on their player row, so a card taken
   * in a robbery cannot be read off anyone else's view.
   */
  resources: ResourceHolding[];
  /**
   * The accessing player's own development cards, and only theirs: the kind of
   * a card is hidden — a victory point above all — while every other player's
   * holding reaches this client as a `developmentCardCount` on their row.
   */
  developmentCards: DevelopmentCard[];
  /** Every player's score from their pieces on the board, public to the table. */
  points: PlayerPoints[];
  /**
   * The player holding the longest road, worth two points and shown to everyone,
   * or null while nobody has a continuous route of five or more. The API is the
   * source of truth; the board only renders it.
   */
  longestRoadPlayerId?: string | null;
  /**
   * The player holding the largest army, worth two points and shown to everyone,
   * or null while nobody has played three knights or more. The API is the source
   * of truth; the board only renders it.
   */
  largestArmyPlayerId?: string | null;
}

/**
 * One development card, as the client reads it: a card in the reader's own hand,
 * or — once the game is won — one of the winner's, which the win reveals to
 * every seat. A kind never travels to anyone else otherwise, so nobody can read
 * a card but its holder before the win.
 */
export interface DevelopmentCard {
  playerId: string;
  card: DevelopmentCardKind;
  /**
   * Whether the card was bought in the turn being played, and so cannot be
   * played in it. Optional because an API that predates the field sends none.
   */
  boughtThisTurn?: boolean;
}

/** The five kinds in the standard deck. */
export type DevelopmentCardKind =
  | 'Knight'
  | 'VictoryPoint'
  | 'RoadBuilding'
  | 'YearOfPlenty'
  | 'Monopoly';

export interface PlacedSettlement {
  vertexId: number;
  playerId: string;
}

/** A city, which supersedes the settlement on its vertex. */
export interface PlacedCity {
  vertexId: number;
  playerId: string;
}

export interface PlacedRoad {
  edgeId: number;
  playerId: string;
}

/** One player's score: a settlement is a point and a city is two. */
export interface PlayerPoints {
  playerId: string;
  points: number;
}

export interface CatanPlayer {
  id: string;
  name: string;
  colour: ColourType | null;
  /**
   * How many resource cards this player holds; public, unlike the cards
   * themselves. Optional because an API that predates the field sends none.
   */
  cardCount?: number;
  /**
   * How many development cards this player holds. Public, like the resource
   * count: the table may know that a card was bought without learning which
   * one. Optional because an API that predates the field sends none.
   */
  developmentCardCount?: number;
  /**
   * How many knights this player has played: public, and the count the largest
   * army reads. Optional because an API that predates the field sends none.
   */
  knightsPlayed?: number;
}

/**
 * A Catan view with every collection a renderer reads guaranteed to be present.
 *
 * The API is the source of truth for the view's shape, but a client and an API
 * that are out of step must not crash the board: an API that predates a field
 * sends a view without it (this is how `settlements` and `roads`, added with
 * setup placement, went missing and `Board` threw on `roads.map`). Defaulting
 * the collections once, here, keeps every `something.map(...)` in the renderers
 * safe and still draws the board out of whatever did arrive.
 */
export const normaliseCatanView = (
  view?: Partial<CatanView> | null,
): CatanView => ({
  creatorPlayerId: view?.creatorPlayerId ?? '',
  accessingPlayerId: view?.accessingPlayerId ?? '',
  lockState: view?.lockState ?? 0,
  state: view?.state ?? { state: StateType.Lobby },
  players: view?.players ?? [],
  topology: {
    cells: view?.topology?.cells ?? [],
    vertices: view?.topology?.vertices ?? [],
    edges: view?.topology?.edges ?? [],
  },
  layout: {
    cells: view?.layout?.cells ?? [],
    ports: view?.layout?.ports ?? [],
  },
  settlements: view?.settlements ?? [],
  cities: view?.cities ?? [],
  roads: view?.roads ?? [],
  robberCellId: view?.robberCellId ?? null,
  resources: view?.resources ?? [],
  developmentCards: view?.developmentCards ?? [],
  points: view?.points ?? [],
  longestRoadPlayerId: view?.longestRoadPlayerId ?? null,
  largestArmyPlayerId: view?.largestArmyPlayerId ?? null,
});
